/**
 * ImageGallery
 *
 * Modal overlay showing the player's uploaded images as a selectable grid.
 * Supports uploading new images and deleting existing ones.
 */

import { useEffect, useRef, useState } from 'react';
import { usePlayerImageStore } from '../stores/playerImageStore';
import { useSessionStore } from '../stores/sessionStore';
import { Icon } from './Icon';
import type { ImagePurpose, PlayerImage } from '@machina/core';

const GOLD = '#d4af37';
const GOLD_DIM = 'rgba(212, 175, 55, 0.3)';
const ACCEPTED_TYPES = 'image/png,image/jpeg,image/webp';

interface ImageGalleryProps {
  purpose: ImagePurpose;
  onSelect: (url: string) => void;
  onClose: () => void;
}

export function ImageGallery({ purpose, onSelect, onClose }: ImageGalleryProps) {
  const { images, isLoading, isUploading, error, loadImages, uploadImage, deleteImage } =
    usePlayerImageStore();
  const userId = useSessionStore((s) => s.userId);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [filter, setFilter] = useState<ImagePurpose | 'all'>(purpose);

  useEffect(() => {
    loadImages();
  }, []);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  });

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !userId) return;
    e.target.value = '';
    try {
      await uploadImage(file, purpose, userId);
    } catch {
      // Error already set in store
    }
  };

  const handleDelete = async (image: PlayerImage) => {
    if (confirmDeleteId !== image.id) {
      setConfirmDeleteId(image.id);
      return;
    }
    setConfirmDeleteId(null);
    await deleteImage(image);
  };

  const handleSelect = (image: PlayerImage) => {
    onSelect(image.publicUrl);
    onClose();
  };

  const filtered = filter === 'all'
    ? images
    : images.filter((i) => i.purpose === filter);

  const title = purpose === 'profile' ? 'Profile Images' : purpose === 'token' ? 'Token Images' : 'Image Gallery';

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'var(--color-background, #0a080c)',
          borderRadius: 8,
          border: `2px solid ${GOLD_DIM}`,
          padding: 24,
          width: 560,
          maxWidth: '90vw',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: `0 20px 60px rgba(0, 0, 0, 0.6), 0 0 40px rgba(212, 175, 55, 0.1)`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{
            margin: 0,
            fontSize: 18,
            fontFamily: 'Georgia, serif',
            fontWeight: 400,
            letterSpacing: '1px',
            color: GOLD,
          }}>
            {title}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              color: 'rgba(255,255,255,0.5)',
              fontSize: 20,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        {/* Upload button */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 16px',
              backgroundColor: 'rgba(212, 175, 55, 0.1)',
              border: `1px solid ${GOLD_DIM}`,
              borderRadius: 4,
              color: GOLD,
              cursor: isUploading ? 'wait' : 'pointer',
              fontFamily: 'Georgia, serif',
              fontSize: 13,
              opacity: isUploading ? 0.5 : 1,
            }}
          >
            <Icon name="upload" size={14} color={GOLD} />
            {isUploading ? 'Uploading...' : 'Upload Image'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES}
            onChange={handleUpload}
            style={{ display: 'none' }}
          />

          {/* Filter tabs */}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
            {(['all', 'profile', 'token'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setFilter(tab)}
                style={{
                  padding: '4px 10px',
                  backgroundColor: filter === tab ? 'rgba(212, 175, 55, 0.15)' : 'transparent',
                  border: `1px solid ${filter === tab ? GOLD_DIM : 'rgba(100, 80, 120, 0.2)'}`,
                  borderRadius: 3,
                  color: filter === tab ? GOLD : 'rgba(255,255,255,0.4)',
                  cursor: 'pointer',
                  fontSize: 11,
                  fontFamily: 'Georgia, serif',
                  textTransform: 'capitalize',
                }}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={{ color: '#ff6b6b', fontSize: 12, marginBottom: 12 }}>
            {error}
          </div>
        )}

        {/* Grid */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'rgba(255,255,255,0.3)', fontFamily: 'Georgia, serif' }}>
              Loading...
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'rgba(255,255,255,0.3)', fontFamily: 'Georgia, serif', fontSize: 13 }}>
              No images yet. Upload one to get started.
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 10,
            }}>
              {filtered.map((image) => (
                <div
                  key={image.id}
                  style={{
                    position: 'relative',
                    aspectRatio: '1',
                    borderRadius: 4,
                    overflow: 'hidden',
                    border: `1px solid rgba(100, 80, 120, 0.3)`,
                    cursor: 'pointer',
                    transition: 'border-color 0.2s',
                  }}
                  onClick={() => handleSelect(image)}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = GOLD; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(100, 80, 120, 0.3)'; }}
                >
                  <img
                    src={image.publicUrl}
                    alt={image.originalName}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                  {/* Delete button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(image);
                    }}
                    style={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      backgroundColor: confirmDeleteId === image.id ? '#ff4444' : 'rgba(0,0,0,0.6)',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: 0.7,
                      transition: 'opacity 0.2s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.7'; }}
                    title={confirmDeleteId === image.id ? 'Click again to confirm' : 'Delete'}
                  >
                    <Icon name="trash" size={12} color="#fff" />
                  </button>
                  {/* Purpose badge */}
                  <div style={{
                    position: 'absolute',
                    bottom: 4,
                    left: 4,
                    padding: '2px 6px',
                    backgroundColor: 'rgba(0,0,0,0.7)',
                    borderRadius: 3,
                    fontSize: 9,
                    color: 'rgba(255,255,255,0.6)',
                    textTransform: 'capitalize',
                  }}>
                    {image.purpose}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
